import React, { ChangeEvent } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import {
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  FormControlLabel,
  Checkbox,
  TextField,
} from '@material-ui/core';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { get, isEmpty } from 'lodash';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from 'date-fns/locale/fr';
import { DATE_PICKET_FORMAT, DATE_FORMAT } from 'constants/global';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';

import messages from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/messages';
import { translate } from 'containers/services/translate';
import { Config } from 'containers/AppContainer/typings';
import { Vehicule } from 'containers/PreOpeningPage/typings';
import {
  ExpertiseState,
  expertType,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import {
  EXPERTISE_FIELD,
  EXPERT_FIELD,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/constants';
import {
  extractFieldValue,
  getExpertiseInfo,
  isCheckedVehicleExpertise,
  isCheckedVictimExpertise,
  resolveLabel,
  shouldDisplayVehicules,
  shouldDisplayVictims,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/utils';
import { getDateIgnoreTimezone } from 'containers/common/LegalProceedings/utils';
import { getVictimFromSagisInfo } from 'containers/PreOpeningPage/utils';
import { VictimType } from 'typings/globalTypes';
import moment from 'moment-timezone';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { JUDGEMENT_ADD_DOCUMENT } from 'containers/data-structure';
import { getVehicleFromSagisInfo } from 'containers/PreOpeningPage/OpposingParty/utils';

export interface ExpertiseFormProps {
  intl: IntlShape;
  expertisesListing: Config[];
  victims: VictimType[];
  vehicules: Vehicule[];
  expertise: ExpertiseState;
  handleSetExpertise: Function;
  index: number;
  canEdit?: boolean;
  setExpertiseDate?: any;
  expertiseDate?: any;
  judgementAddPronouncing?: any;
  sagisVictimInfo: any;
  sagisOpposingParties: any;
  isBasicManagement?: boolean;
  victimIdsJom?: any;
}

const ExpertiseForm: React.FC<ExpertiseFormProps> = (props): JSX.Element => {
  const expertise = { ...props.expertise };
  const handleChangeExpertise = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    expertise[EXPERTISE_FIELD.EXPERTISE_ID] = String(event.target.value);
    expertise[EXPERTISE_FIELD.VEHICULES].clear();
    expertise[EXPERTISE_FIELD.VICTIMS].clear();
    expertise[EXPERTISE_FIELD.EXPERTISES].clear();

    props.handleSetExpertise(expertise);
  };

  const handleChangeDate = (
    victimId: number | null,
    vehiculeId: number | null,
  ) => (date: any) => {
    const newState: Set<expertType> = new Set(
      expertise[EXPERTISE_FIELD.EXPERTISES],
    );
    let label = resolveLabel(victimId);
    const itemId = victimId || vehiculeId;
    let expertiseInfo = getExpertiseInfo(itemId, label, newState) as expertType;
    const theDate = getDateIgnoreTimezone(date);
    const expertiseDate = moment(theDate as Date).format(DATE_FORMAT);
    if (isEmpty(expertiseInfo)) {
      newState.add(({
        [label]: itemId,
        expertiseDate,
        address: '',
        expertName: '',
      } as unknown) as expertType);
    } else {
      expertiseInfo[EXPERT_FIELD.DATE] = expertiseDate;
    }

    props.handleSetExpertise({ ...expertise, expertises: newState });

    if (
      props.expertiseDate === null ||
      new Date(date) < new Date(props.expertiseDate)
    ) {
      props.setExpertiseDate(date);
    }
  };
  const handleChangeExpert = (
    victimId: number | null,
    vehiculeId: number | null,
  ) => (event: ChangeEvent<{ value: string; name: string }>) => {
    let label = resolveLabel(victimId);
    const itemId = victimId || vehiculeId;
    const { name, value } = event.target;

    const newState: Set<expertType> = new Set(
      expertise[EXPERTISE_FIELD.EXPERTISES],
    );
    let expertiseInfo = getExpertiseInfo(itemId, label, newState) as expertType;

    if (isEmpty(expertiseInfo)) {
      newState.add(({
        [label]: itemId,
        [name]: value,
        isDeleted: false,
      } as unknown) as expertType);
    } else {
      expertiseInfo[name] = event.target.value;
    }

    props.handleSetExpertise({ ...expertise, expertises: newState });
  };

  const handleChangeCheckBox = (field: keyof ExpertiseState) => (
    event: React.ChangeEvent<{ value: unknown; checked: boolean }>,
  ) => {
    const id = Number(event.target.value);
    if (event.target.checked) {
      (expertise[field] as Set<number>).add(id);
      if (field === EXPERTISE_FIELD.VICTIMS) {
        const existingObj = [...expertise[EXPERTISE_FIELD.EXPERTISES]].find(
          obj => {
            return Number(get(obj, 'victimId', '')) === Number(id);
          },
        );
        if (!existingObj) {
          expertise[EXPERTISE_FIELD.EXPERTISES].add({
            victimId: id,
            isNew: true,
            isDeleted: false,
          } as any);
        } else {
          existingObj.isDeleted = false;
        }
      }
      if (field === EXPERTISE_FIELD.VEHICULES) {
        const existingObj = [...expertise[EXPERTISE_FIELD.EXPERTISES]]?.find(
          obj => {
            return Number(get(obj, 'vehiculeId', '')) === Number(id);
          },
        );
        if (!existingObj) {
          expertise[EXPERTISE_FIELD.EXPERTISES].add({
            vehiculeId: id,
            isNew: true,
            isDeleted: false,
          } as any);
        } else {
          existingObj.isDeleted = false;
        }
      }
    } else {
      for (const obj of expertise[EXPERTISE_FIELD.EXPERTISES]) {
        if (get(obj, 'victimId', obj?.vehiculeId) === id) {
          obj.isDeleted = true;
          obj.isNew &&
            obj.isDeleted &&
            expertise[EXPERTISE_FIELD.EXPERTISES].delete(obj);
          break;
        }
      }
    }
    props.handleSetExpertise(expertise);
  };
  const expertiseId = get(props.expertise, [EXPERTISE_FIELD.EXPERTISE_ID], '');

  const disableUntil = (date: any) => {
    if (
      props.judgementAddPronouncing &&
      date >
        new Date(
          props.judgementAddPronouncing[JUDGEMENT_ADD_DOCUMENT.JUDGEMENT_DATE],
        )
    )
      return false;
    return true;
  };
  return (
    <main>
      <section className="expertise">
        <FormControl className="field">
          <InputLabel>{translate(messages.expertiseField, props)}</InputLabel>
          <Select
            disabled={!props.canEdit}
            value={expertiseId}
            onChange={handleChangeExpertise}
          >
            {!isEmpty(props.expertisesListing) &&
              props.expertisesListing.map((expertise: Config) => (
                <MenuItem value={expertise.id} key={expertise.id}>
                  {expertise.title}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </section>
      {shouldDisplayVictims(String(expertiseId)) && !isEmpty(props.victims) && (
        <section className="checkbox-section">
          <div className="checkbox-title">
            {translate(messages.selectVictims, props)}
          </div>
          <div className="checkbox-wrapper">
            {props.victims.map(victim => (
              <div className="victim-wrapper">
                <div key={victim.id} className="checkbox-bloc">
                  <FormControlLabel
                    value={get(props.expertise, EXPERTISE_FIELD.VICTIMS)}
                    control={
                      <Checkbox
                        color="primary"
                        className="checkbox"
                        value={victim.id}
                        onChange={handleChangeCheckBox(EXPERTISE_FIELD.VICTIMS)}
                        checked={isCheckedVictimExpertise(
                          victim.id as number,
                          get(props.expertise, 'expertises', []),
                        )}
                        disabled={
                          !props.canEdit &&
                          !isEmpty(props.victimIdsJom) &&
                          props.victimIdsJom.includes(victim.id) &&
                          isCheckedVictimExpertise(
                            victim.id as number,
                            get(props.expertise, 'expertises', []),
                          )
                        }
                      />
                    }
                    label={
                      featureService.hasFeature(
                        FEATURES.MIGRATION_CLAIM_INFO,
                      ) && !props.isBasicManagement
                        ? `${get(
                            getVictimFromSagisInfo(
                              get(victim, 'victimKey'),
                              props.sagisVictimInfo,
                            ),
                            'victimFirstName',
                            '',
                          )} ${get(
                            getVictimFromSagisInfo(
                              get(victim, 'victimKey'),
                              props.sagisVictimInfo,
                            ),
                            'victimLastName',
                            '',
                          )}`
                        : `${victim.firstName} ${victim.lastName}`
                    }
                  />
                </div>
                {featureService.hasFeature(
                  FEATURES.JUDGEMENT_ADD_EXPERT_INFO,
                ) &&
                  isCheckedVictimExpertise(
                    victim.id as number,
                    get(props.expertise, 'expertises', []),
                  ) && (
                    <>
                      <div className="wrapper-expert">
                        <TextField
                          label={translate(messages.nameExpertField, props)}
                          name={EXPERT_FIELD.NAME}
                          value={extractFieldValue(
                            victim.id as number,
                            'victimId',
                            EXPERT_FIELD.NAME,
                            expertise[EXPERTISE_FIELD.EXPERTISES],
                          )}
                          onChange={handleChangeExpert(
                            victim.id as number,
                            null,
                          )}
                        />
                      </div>
                      <div className="wrapper-expert">
                        <TextField
                          label={translate(messages.adresseExpertField, props)}
                          name={EXPERT_FIELD.ADRESSE}
                          value={extractFieldValue(
                            victim.id as number,
                            'victimId',
                            EXPERT_FIELD.ADRESSE,
                            expertise[EXPERTISE_FIELD.EXPERTISES],
                          )}
                          onChange={handleChangeExpert(
                            victim.id as number,
                            null,
                          )}
                        />
                      </div>
                      <div className="wrapper-expert">
                        <MuiPickersUtilsProvider
                          utils={DateFnsUtils}
                          locale={frLocale}
                        >
                          <KeyboardDatePicker
                            autoOk
                            variant="inline"
                            invalidDateMessage=""
                            format={DATE_PICKET_FORMAT}
                            shouldDisableDate={disableUntil}
                            id="form-judgement-date"
                            onChange={handleChangeDate(
                              victim.id as number,
                              null,
                            )}
                            name={EXPERT_FIELD.DATE}
                            value={dateStringToLocalDate(
                              extractFieldValue(
                                victim.id as number,
                                'victimId',
                                EXPERT_FIELD.DATE,
                                expertise[EXPERTISE_FIELD.EXPERTISES],
                              ),
                            )}
                            label={translate(
                              messages.dateExpertiseField,
                              props,
                            )}
                            error={
                              extractFieldValue(
                                victim.id as number,
                                'victimId',
                                EXPERT_FIELD.DATE,
                                expertise[EXPERTISE_FIELD.EXPERTISES],
                              ) === 'Invalid date'
                            }
                            helperText={
                              extractFieldValue(
                                victim.id as number,
                                'victimId',
                                EXPERT_FIELD.DATE,
                                expertise[EXPERTISE_FIELD.EXPERTISES],
                              ) === 'Invalid date' &&
                              'Merci de saisir le bon format de la date'
                            }
                          />
                        </MuiPickersUtilsProvider>
                      </div>
                    </>
                  )}
              </div>
            ))}
          </div>
        </section>
      )}
      {shouldDisplayVehicules(String(expertiseId)) &&
        !isEmpty(props.vehicules) && (
          <section className="checkbox-section">
            <div className="checkbox-title">
              {translate(
                messages.selectOpposingPartyRegistrationNumbers,
                props,
              )}
            </div>
            <div className="checkbox-wrapper">
              {props.vehicules.map(vehicule => (
                <div className="victim-wrapper">
                  <div key={vehicule.id} className="checkbox-bloc">
                    <FormControlLabel
                      control={
                        <Checkbox
                          className="checkbox"
                          color="primary"
                          value={vehicule.id}
                          onChange={handleChangeCheckBox(
                            EXPERTISE_FIELD.VEHICULES,
                          )}
                          checked={isCheckedVehicleExpertise(
                            vehicule.id as number,
                            get(props.expertise, 'expertises', []),
                          )}
                        />
                      }
                      label={
                        featureService.hasFeature(
                          FEATURES.MIGRATION_CLAIM_INFO,
                        ) &&
                        !isEmpty(
                          getVehicleFromSagisInfo(
                            get(vehicule, 'partKey'),
                            props.sagisOpposingParties,
                          ),
                        )
                          ? get(
                              getVehicleFromSagisInfo(
                                get(vehicule, 'partKey'),
                                props.sagisOpposingParties,
                              ),
                              'registrationNumber',
                            )
                          : vehicule.registrationNumber
                      }
                    />
                  </div>
                  {featureService.hasFeature(
                    FEATURES.JUDGEMENT_ADD_EXPERT_INFO,
                  ) &&
                    isCheckedVehicleExpertise(
                      vehicule.id as number,
                      get(props.expertise, 'expertises', []),
                    ) && (
                      <>
                        <div className="wrapper-expert">
                          <TextField
                            label={translate(messages.nameExpertField, props)}
                            name={EXPERT_FIELD.NAME}
                            value={extractFieldValue(
                              vehicule.id as number,
                              'vehiculeId',
                              EXPERT_FIELD.NAME,
                              expertise[EXPERTISE_FIELD.EXPERTISES],
                            )}
                            onChange={handleChangeExpert(
                              null,
                              vehicule.id as number,
                            )}
                          />
                        </div>
                        <div className="wrapper-expert">
                          <TextField
                            label={translate(
                              messages.adresseExpertField,
                              props,
                            )}
                            name={EXPERT_FIELD.ADRESSE}
                            value={extractFieldValue(
                              vehicule.id as number,
                              'vehiculeId',
                              EXPERT_FIELD.ADRESSE,
                              expertise[EXPERTISE_FIELD.EXPERTISES],
                            )}
                            onChange={handleChangeExpert(
                              null,
                              vehicule.id as number,
                            )}
                          />
                        </div>
                        <div className="wrapper-expert">
                          <MuiPickersUtilsProvider
                            utils={DateFnsUtils}
                            locale={frLocale}
                          >
                            <KeyboardDatePicker
                              autoOk
                              variant="inline"
                              name={EXPERT_FIELD.DATE}
                              invalidDateMessage=""
                              format={DATE_PICKET_FORMAT}
                              id="form-judgement-date"
                              onChange={handleChangeDate(
                                null,
                                vehicule.id as number,
                              )}
                              minDate={get(
                                props,
                                'judgementAddPronouncing.judgementDate',
                              )}
                              value={dateStringToLocalDate(
                                extractFieldValue(
                                  vehicule.id as number,
                                  'vehiculeId',
                                  EXPERT_FIELD.DATE,
                                  expertise[EXPERTISE_FIELD.EXPERTISES],
                                ),
                              )}
                              label={translate(
                                messages.dateExpertiseField,
                                props,
                              )}
                              error={
                                extractFieldValue(
                                  vehicule.id as number,
                                  'vehiculeId',
                                  EXPERT_FIELD.DATE,
                                  expertise[EXPERTISE_FIELD.EXPERTISES],
                                ) === 'Invalid date'
                              }
                              helperText={
                                extractFieldValue(
                                  vehicule.id as number,
                                  'vehiculeId',
                                  EXPERT_FIELD.DATE,
                                  expertise[EXPERTISE_FIELD.EXPERTISES],
                                ) === 'Invalid date' &&
                                'Merci de saisir le bon format de la date'
                              }
                            />
                          </MuiPickersUtilsProvider>
                        </div>
                      </>
                    )}
                </div>
              ))}
            </div>
          </section>
        )}
    </main>
  );
};

ExpertiseForm.defaultProps = {
  canEdit: true,
};

export default injectIntl(ExpertiseForm);
